import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { appleEase } from "@/lib/motion";
import { useAuth, API } from "@/context/AuthContext";
import { setSellerIntent, resolvePostLoginPath } from "@/lib/authRedirect";
import { TangigMark } from "@/components/brand/TangigLogo";
import { ArrowLeft, Star, Shield, Zap, Lock, Mail, Eye, EyeOff, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { TEAL, FONT, RADIUS, SHADOW, TRANSITION, colors } from "@/lib/tokens";

/* ── Aliases ───────────────────────────────────────────────── */
const BG_DARK = colors.backgroundDark;
const CARD_BG = colors.backgroundCard;

/* ── Phone Mockup ──────────────────────────────────────────── */
function PhoneMockup() {
  return (
    <div style={{ width: "260px", margin: "0 auto" }}>
      {/* WebP a fond transparent, recadre sur le telephone (bbox alpha) : il se
          centre donc tout seul dans sa colonne, sans marge morte d'un cote. */}
      <img
        src="/connexion-systeme.webp"
        alt="Notification d'une vente generee par un systeme TANGIG"
        style={{ width: "100%", display: "block", margin: "0 auto" }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
export default function Auth() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const intent = searchParams.get("intent");

  /* Mémorise l'intention "devenir vendeur" pour qu'elle survive
     à l'aller-retour OAuth Google (sinon le query param est perdu). */
  useEffect(() => {
    if (intent === "seller") setSellerIntent();
  }, [intent]);

  /* login | register | null */
  const [emailMode, setEmailMode]     = useState(null);
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [confirmPwd, setConfirmPwd]   = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate(resolvePostLoginPath(user.role));
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (error) toast.error("Erreur lors de la connexion. Réessayez.");
  }, [error]);

  const handleGoogleLogin = () => {
    const redirectUri = window.location.origin + "/auth/callback";
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile`;
  };


  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Veuillez remplir tous les champs."); return; }
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      // Le backend renvoie { access_token, token_type, user } : on stocke le
      // token puis on rafraîchit l'auth — le changement de `user` déclenche la
      // redirection (resolvePostLoginPath, qui respecte l'intention vendeur).
      localStorage.setItem("token", res.data.access_token);
      await checkAuth();
      toast.success("Connexion réussie !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email ou mot de passe incorrect.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Veuillez remplir tous les champs."); return; }
    if (password !== confirmPwd) { toast.error("Les mots de passe ne correspondent pas."); return; }
    setLoginLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { email, password });
      toast.success("Compte créé ! Vous pouvez maintenant vous connecter.");
      setEmailMode("login");
      setConfirmPwd("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du compte.");
    } finally {
      setLoginLoading(false);
    }
  };

  const resetEmailMode = () => {
    setEmailMode(null);
    setEmail(""); setPassword(""); setConfirmPwd(""); setShowPassword(false);
  };

  /* ── Render ── */
  return (
    <div style={{ minHeight: "100vh", display: "flex", overflow: "hidden" }}>

      {/* ══════════════════════════════════════════════════
          COLONNE GAUCHE — sombre
      ══════════════════════════════════════════════════ */}
      <motion.div
        className="hidden lg:flex lg:w-1/2"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, ease: appleEase }}
        style={{ backgroundColor: BG_DARK, flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}
      >
        {/* Logo top-left — le symbole seul, pas le mot. */}
        <Link to="/" style={{ position: "absolute", top: "32px", left: "32px", display: "block" }} aria-label="TANGIG — accueil">
          <TangigMark size={22} color="rgba(255,255,255,0.62)" title={null} />
        </Link>

        {/* Content */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "0 48px", maxWidth: "520px" }}>

          {/* Titre */}
          <h2 style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "clamp(1.8rem, 2.8vw, 3rem)",
            letterSpacing: "-0.032em",
            lineHeight: 1.15,
            color: "#FFFFFF",
            margin: "48px 0 16px",
          }}>
            Les notifications qui<br />
            <span style={{ color: TEAL }}>changent tout.</span>
          </h2>

          {/* Trait séparateur premium */}
          <div style={{ width: "40px", height: "1.5px", backgroundColor: "rgba(255,255,255,0.18)", borderRadius: "2px", margin: "0 auto 16px" }} />

          {/* Accroche déplacée */}
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: "13px", color: "rgba(255,255,255,0.42)", margin: "0 0 40px", whiteSpace: "nowrap", letterSpacing: "0.01em" }}>
            Rejoignez les créateurs qui ont compris que 2026 n'attend pas.
          </p>

          {/* Phone */}
          <PhoneMockup />

          {/* Trust signals */}
          <div style={{ marginTop: "28px", display: "flex", flexDirection: "column", gap: "12px", textAlign: "left", width: "100%", maxWidth: "300px" }}>
            {[
              "Votre système se vend pendant que vous dormez",
              "Zéro stock. Zéro livraison. 100% automatique.",
            ].map((txt) => (
              <div key={txt} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <Check size={15} strokeWidth={2} style={{ color: TEAL, flexShrink: 0, marginTop: "2px" }} />
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: "14px", color: "rgba(255,255,255,0.62)", lineHeight: 1.55 }}>{txt}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ══════════════════════════════════════════════════
          COLONNE DROITE — formulaire
      ══════════════════════════════════════════════════ */}
      <motion.div
        className="w-full lg:w-1/2"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: appleEase, delay: 0.15 }}
        style={{ backgroundColor: "#F7F9F8", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", position: "relative" }}
      >
        <div style={{ position: "relative", zIndex: 10, width: "100%", maxWidth: "420px" }}>

          {/* Back link */}
          <Link
            to="/"
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#6B7A7A", textDecoration: "none", marginBottom: "28px", fontFamily: "Inter, sans-serif", transition: "color 0.15s" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#0E1A18"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#6B7A7A"; }}
          >
            <ArrowLeft size={15} strokeWidth={1.75} />
            Retour à l'accueil
          </Link>

          {/* ── CARD ── */}
          <div style={{
            backgroundColor: CARD_BG,
            border: `1px solid ${colors.border}`,
            borderRadius: RADIUS.md,
            boxShadow: SHADOW.modal,
            overflow: "hidden",
          }}>

            {/* Card header */}
            <div style={{ padding: "28px 32px 24px", textAlign: "center", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              {/* Le symbole remplace le mot : sur mobile la colonne sombre est
                  masquee, c'est le seul rappel de marque de la page. */}
              <TangigMark size={20} color="#0E1A18" style={{ margin: "0 auto" }} />
              <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "22px", color: "#0E1A18", letterSpacing: "-0.02em", margin: "10px 0 6px" }}>
                Bienvenue
              </h1>
              <p style={{ fontFamily: "Inter, sans-serif", fontSize: "14px", color: "#6B7A7A", margin: 0 }}>
                Connectez-vous pour accéder aux Systèmes
              </p>
            </div>

            {/* Card body */}
            <div style={{ padding: "28px 32px" }}>

              {emailMode === "login" ? (
                /* ── Formulaire connexion ── */
                <form onSubmit={handleEmailLogin} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div>
                    <label style={labelStyle}>Votre adresse email</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="vous@exemple.com" required style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Mot de passe</label>
                    <div style={{ position: "relative" }}>
                      <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required style={{ ...inputStyle, paddingRight: "42px" }} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9AABAB", padding: 0 }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" disabled={loginLoading} style={submitBtnStyle}>
                    {loginLoading ? <span style={{ width: "16px", height: "16px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> : <>Se connecter <ArrowRight size={15} strokeWidth={1.75} /></>}
                  </button>
                  <p style={{ textAlign: "center", fontFamily: "Inter, sans-serif", fontSize: "13px", color: "#9AABAB", margin: "4px 0 0" }}>
                    Pas encore de compte ?{" "}
                    <button type="button" onClick={() => setEmailMode("register")} style={{ background: "none", border: "none", color: TEAL, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: "13px", padding: 0 }}>
                      Créer un compte
                    </button>
                  </p>
                  <button type="button" onClick={resetEmailMode} style={{ background: "none", border: "none", color: "#9AABAB", fontFamily: "Inter, sans-serif", fontSize: "13px", cursor: "pointer", paddingTop: "4px" }}>
                    ← Retour
                  </button>
                </form>

              ) : emailMode === "register" ? (
                /* ── Formulaire inscription ── */
                <form onSubmit={handleEmailRegister} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div>
                    <label style={labelStyle}>Votre adresse email</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="vous@exemple.com" required style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Mot de passe</label>
                    <div style={{ position: "relative" }}>
                      <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required style={{ ...inputStyle, paddingRight: "42px" }} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9AABAB", padding: 0 }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Confirmer le mot de passe</label>
                    <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} placeholder="••••••••" required style={inputStyle} />
                  </div>
                  <button type="submit" disabled={loginLoading} style={submitBtnStyle}>
                    {loginLoading ? <span style={{ width: "16px", height: "16px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> : <>Créer mon compte <ArrowRight size={15} strokeWidth={1.75} /></>}
                  </button>
                  <p style={{ textAlign: "center", fontFamily: "Inter, sans-serif", fontSize: "13px", color: "#9AABAB", margin: "4px 0 0" }}>
                    Déjà un compte ?{" "}
                    <button type="button" onClick={() => setEmailMode("login")} style={{ background: "none", border: "none", color: TEAL, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: "13px", padding: 0 }}>
                      Se connecter
                    </button>
                  </p>
                  <button type="button" onClick={resetEmailMode} style={{ background: "none", border: "none", color: "#9AABAB", fontFamily: "Inter, sans-serif", fontSize: "13px", cursor: "pointer", paddingTop: "4px" }}>
                    ← Retour
                  </button>
                </form>

              ) : (
                /* ── Boutons d'authentification ── */
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

                  {/* Google */}
                  <button onClick={handleGoogleLogin} data-testid="google-login-btn" style={socialBtnStyle}>
                    <svg width="17" height="17" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continuer avec Google
                  </button>

                  {/* Separator */}
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "2px 0" }}>
                    <div style={{ flex: 1, height: "1px", backgroundColor: "rgba(0,0,0,0.07)" }} />
                    <span style={{ fontFamily: "Inter, sans-serif", fontSize: "12px", color: "#AABABA" }}>ou</span>
                    <div style={{ flex: 1, height: "1px", backgroundColor: "rgba(0,0,0,0.07)" }} />
                  </div>

                  {/* Email register */}
                  <button onClick={() => setEmailMode("register")} style={outlineBtnStyle}>
                    <Mail size={15} strokeWidth={1.75} style={{ color: TEAL }} />
                    Créer un compte avec email
                  </button>

                  {/* Email login link */}
                  <p style={{ textAlign: "center", fontFamily: "Inter, sans-serif", fontSize: "13px", color: "#9AABAB", margin: "2px 0 0" }}>
                    Déjà un compte ?{" "}
                    <button onClick={() => setEmailMode("login")} style={{ background: "none", border: "none", color: TEAL, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: "13px", padding: 0 }}>
                      Se connecter
                    </button>
                  </p>

                  {/* Benefits badges */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
                    {[
                      { icon: <Star size={15} strokeWidth={1.75} style={{ color: TEAL }} />,   text: "Accédez à des actifs business premium" },
                      { icon: <Shield size={15} strokeWidth={1.75} style={{ color: TEAL }} />, text: "Paiement sécurisé via Stripe"           },
                      { icon: <Zap size={15} strokeWidth={1.75} style={{ color: TEAL }} />,    text: "Téléchargement immédiat après achat"   },
                    ].map((item, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "11px 14px", backgroundColor: "#F4F6F5", border: "1px solid #E3E7E5", borderRadius: "10px" }}>
                        <div style={{ flexShrink: 0 }}>{item.icon}</div>
                        <span style={{ fontFamily: "Inter, sans-serif", fontSize: "13px", color: "#3A4A4A", fontWeight: 500 }}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Card footer */}
            <div style={{ padding: "16px 32px 22px", textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontFamily: "Inter, sans-serif", fontSize: "12px", color: "#9AABAB" }}>
                <Lock size={12} strokeWidth={1.75} />
                Connexion 100% sécurisée · Données protégées RGPD
              </div>
            </div>
          </div>

          {/* Legal */}
          <p style={{ marginTop: "20px", textAlign: "center", fontFamily: "Inter, sans-serif", fontSize: "12px", color: "#9AABAB", padding: "0 16px" }}>
            En continuant, vous acceptez nos{" "}
            <Link to="/legal/cgu" style={{ color: TEAL, textDecoration: "none" }}>CGU</Link>
            {" "}et notre{" "}
            <Link to="/legal/confidentialite" style={{ color: TEAL, textDecoration: "none" }}>Politique de confidentialité</Link>
          </p>
        </div>
      </motion.div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Styles partagés ───────────────────────────────────────── */
const labelStyle = {
  display: "block",
  fontFamily: FONT,
  fontSize: "12px",
  fontWeight: 500,
  color: "#6B7A7A",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: RADIUS.sm,
  border: "1.5px solid rgba(0,0,0,0.09)",
  backgroundColor: colors.backgroundSubtle,
  fontFamily: FONT,
  fontSize: "14px",
  color: "#0E1A18",
  outline: "none",
  boxSizing: "border-box",
  transition: `border-color ${TRANSITION.fast}`,
};

const submitBtnStyle = {
  width: "100%",
  padding: "13px",
  borderRadius: RADIUS.sm,
  border: "none",
  backgroundColor: TEAL,
  color: "#FFFFFF",
  fontFamily: FONT,
  fontSize: "15px",
  fontWeight: 500,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  transition: `background ${TRANSITION.fast}`,
  marginTop: "4px",
};

const socialBtnStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  padding: "13px 16px",
  backgroundColor: "#FFFFFF",
  border: "1.5px solid rgba(0,0,0,0.09)",
  borderRadius: RADIUS.sm,
  fontFamily: FONT,
  fontSize: "14px",
  fontWeight: 500,
  color: "#0E1A18",
  cursor: "pointer",
  transition: `background ${TRANSITION.fast}, border-color ${TRANSITION.fast}`,
};

const outlineBtnStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "9px",
  padding: "13px 16px",
  backgroundColor: "#F4F6F5",
  border: "1.5px solid #E3E7E5",
  borderRadius: RADIUS.sm,
  fontFamily: FONT,
  fontSize: "14px",
  fontWeight: 500,
  color: "#0E1A18",
  cursor: "pointer",
  transition: `background ${TRANSITION.fast}`,
};
